import React, { RefObject } from "react";
import { Flex, Grid, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppSelect from "@/features/line-sheet-sets/app-select";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useScrolledFromTop from "@/shared/hooks/useScrolledFromTop";
import PathPickScrollToTopCircleButton from "@/features/pathpick/components/ui/PathPickScrollToTopCircleButton";

interface Props {
  total: number;
  pageNumber: number;
  pageSize: number;
  pageSizeOptions?: number[];
  // sticky footer 안에서 쓸 때는 "fixed"를 준다. absolute면 닫힌 팝오버가
  // sticky footer를 기준 박스로 삼아 스크롤 영역을 자기 높이만큼 늘린다.
  popoverStrategy?: "fixed" | "absolute";
  // 페이지의 스크롤 Box — 넘기면 모바일에서 스크롤이 내려가 있는 동안 "맨 위로" 버튼을 띄운다.
  scrollContainerRef?: RefObject<HTMLDivElement>;
  onChangePage: (page: number) => void;
  onChangePageSize: (size: number) => void;
}

// AppButton medium 기본 패딩(4px 32px)은 이동 버튼 네 개가 나란히 서기엔 넓다.
const NAV_BUTTON_PADDING = "4px 20px";

// 스크롤 여부 구독을 prop 유무에 따라 조건부로 걸 수 없어 칸 컴포넌트로 뗀다.
function ScrollToTopSlot({
  containerRef,
}: {
  containerRef: RefObject<HTMLDivElement>;
}) {
  const isScrolled = useScrolledFromTop(containerRef);
  return (
    <PathPickScrollToTopCircleButton
      containerRef={containerRef}
      isVisible={isScrolled}
    />
  );
}

// pathpick 공용 페이지네이션 (상품 리스트/담은 상품 리스트 공용)
function PathPickPagination({
  total,
  pageNumber,
  pageSize,
  pageSizeOptions = [20, 50, 100],
  popoverStrategy,
  scrollContainerRef,
  onChangePage,
  onChangePageSize,
}: Props) {
  const { t } = useI18nHelper();

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const end = Math.min(pageNumber * pageSize, total);

  return (
    // PC는 한 줄 [요약 …… 페이지 크기 · 이동], 모바일은 두 줄
    // [요약 …… 페이지 크기] / [이동]으로 배치를 명시한다. flex-wrap에 맡기면
    // 폭에 따라 덩어리 안에서 다시 접혀 "다음" 버튼만 셋째 줄로 떨어진다.
    // 스크롤 Box를 넘겨받으면 모바일 첫 줄 가운데 빈 칸에 "맨 위로" 버튼을 둔다 —
    // 양옆을 1fr로 나눠야 요약 길이와 무관하게 버튼이 가운데에 선다.
    <Grid
      templateAreas={{
        base: scrollContainerRef
          ? `"summary top size" "nav nav nav"`
          : `"summary size" "nav nav"`,
        md: `"summary size nav"`,
      }}
      templateColumns={{
        base: scrollContainerRef ? "1fr auto 1fr" : "1fr auto",
        md: "1fr auto auto",
      }}
      alignItems={"center"}
      columnGap={"8px"}
      rowGap={"8px"}
    >
      <Text
        gridArea={"summary"}
        minWidth={0}
        textStyle={"body-sm-regular"}
        color={"#8F8F8C"}
      >
        {t("path_pick:pagination.summary", {
          total,
          start,
          end,
        })}
      </Text>
      {/* PC 배치에는 top 칸이 없어 감춰야 자동 배치로 새 칸이 생기지 않는다. */}
      {scrollContainerRef && (
        <Flex gridArea={"top"} display={{ base: "flex", md: "none" }}>
          <ScrollToTopSlot containerRef={scrollContainerRef} />
        </Flex>
      )}
      <Flex
        gridArea={"size"}
        justifySelf={{ base: "end", md: "auto" }}
        gap={"8px"}
        alignItems={"center"}
      >
        <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
          {t("path_pick:pagination.per_page")}
        </Text>
        <AppSelect<number>
          width={"88px"}
          popoverStrategy={popoverStrategy}
          value={pageSize}
          options={pageSizeOptions.map((size) => ({
            value: size,
            name: t("path_pick:pagination.page_size_unit", {
              count: size,
            }),
          }))}
          name={t("path_pick:pagination.page_size_unit", {
            count: pageSize,
          })}
          isClearable={false}
          onSelect={(value) => {
            if (value !== undefined) {
              onChangePageSize(value);
            }
          }}
        />
      </Flex>
      <Flex
        gridArea={"nav"}
        gap={"8px"}
        alignItems={"center"}
        justifySelf={{ base: "center", md: "auto" }}
      >
        <AppButton
          variant={"white"}
          isDisabled={pageNumber <= 1}
          onClick={() => onChangePage(1)}
          chakraProps={{
            p: NAV_BUTTON_PADDING,
            "aria-label": t("path_pick:pagination.first"),
          }}
        >
          <CustomIcon src={"chevron-double-left"} width={16} height={16} />
        </AppButton>
        <AppButton
          variant={"white"}
          isDisabled={pageNumber <= 1}
          onClick={() => onChangePage(pageNumber - 1)}
          chakraProps={{ p: NAV_BUTTON_PADDING }}
        >
          <Flex gap={"4px"} alignItems={"center"}>
            <CustomIcon src={"chevron-left"} width={16} height={16} />
            <Text textStyle={"body-sm-bold"}>
              {t("path_pick:pagination.prev")}
            </Text>
          </Flex>
        </AppButton>
        <Text textStyle={"body-sm-medium"} color={"#444440"}>
          {`${pageNumber} / ${totalPages}`}
        </Text>
        <AppButton
          variant={"white"}
          isDisabled={pageNumber >= totalPages}
          onClick={() => onChangePage(pageNumber + 1)}
          chakraProps={{ p: NAV_BUTTON_PADDING }}
        >
          <Flex gap={"4px"} alignItems={"center"}>
            <Text textStyle={"body-sm-bold"}>
              {t("path_pick:pagination.next")}
            </Text>
            <CustomIcon src={"chevron-right"} width={16} height={16} />
          </Flex>
        </AppButton>
        <AppButton
          variant={"white"}
          isDisabled={pageNumber >= totalPages}
          onClick={() => onChangePage(totalPages)}
          chakraProps={{
            p: NAV_BUTTON_PADDING,
            "aria-label": t("path_pick:pagination.last"),
          }}
        >
          <CustomIcon src={"chevron-double-right"} width={16} height={16} />
        </AppButton>
      </Flex>
    </Grid>
  );
}

export default PathPickPagination;
